import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

// Clean a string for use in a file name.
const sanitizeFileName = (s) =>
  (s || "controle")
    .toString()
    .replace(/[^a-zA-Z0-9._-]/g, "_")
    .replace(/_{2,}/g, "_")
    .replace(/^_+|_+$/g, "");

// Turn a champ's `content` array into a single cell value.
// For "imgtext" fields, content[0] is the text and content[1] is the image URL
// (see downloadAllControlImages.js), so we keep only the text.
const champToValue = (champ) => {
  const content = champ?.content;
  if (content === null || content === undefined) return "";
  if (!Array.isArray(content)) return String(content);
  if (champ.type === "imgtext") return content.length > 0 ? String(content[0] ?? "") : "";
  return content
    .filter((v) => v !== null && v !== undefined && v !== "")
    .join(", ");
};

/**
 * Export a single control sheet to an Excel file (one row, fields as columns).
 *
 * @param {object} control   The control instance (with `cochamps`).
 * @param {object} selection { [categoryName]: { fields: { [fieldName]: boolean } } }
 *                           Only categories/fields flagged true are exported.
 * @param {object} folder    The parent folder/dossier (used for identity columns).
 */
const exportControlToExcel = async (control, selection, folder) => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Contrôle");

  // Leading identity columns so each row is meaningful on its own and several
  // controls can later be stacked into one sheet (matches the example Excel).
  const leadColumns = [
    { header: "Dossier", value: folder?.nom || "" },
    { header: "Contrôle", value: control?.name || "" },
    {
      header: "Date",
      value: control?.date?.seconds
        ? new Date(control.date.seconds * 1000).toLocaleDateString()
        : "",
    },
  ];

  // Build the selected (category -> fields) groups, preserving the control order.
  const groups = [];
  (control?.cochamps || []).forEach((cochamp) => {
    const catSelection = selection?.[cochamp.name];
    if (!catSelection) return;
    const fields = [];
    (cochamp.champs || []).forEach((champ) => {
      if (catSelection.fields?.[champ.name]) {
        fields.push({ name: champ.name, value: champToValue(champ) });
      }
    });
    if (fields.length > 0) groups.push({ category: cochamp.name, fields });
  });

  // Three rows: category header (merged), field header, values.
  const categoryRow = [];
  const fieldRow = [];
  const valueRow = [];

  leadColumns.forEach((col) => {
    categoryRow.push("Infos");
    fieldRow.push(col.header);
    valueRow.push(col.value);
  });
  groups.forEach((group) => {
    group.fields.forEach((field) => {
      categoryRow.push(group.category);
      fieldRow.push(field.name);
      valueRow.push(field.value);
    });
  });

  sheet.addRow(categoryRow);
  sheet.addRow(fieldRow);
  sheet.addRow(valueRow);

  // Merge each category header across the columns of its fields.
  const spans = [{ start: 1, end: leadColumns.length }];
  let cursor = leadColumns.length + 1;
  groups.forEach((group) => {
    spans.push({ start: cursor, end: cursor + group.fields.length - 1 });
    cursor += group.fields.length;
  });
  spans.forEach((span) => {
    if (span.end > span.start) sheet.mergeCells(1, span.start, 1, span.end);
  });

  // Light styling.
  const headerCat = sheet.getRow(1);
  headerCat.font = { bold: true };
  headerCat.alignment = { horizontal: "center", vertical: "middle" };
  sheet.getRow(2).font = { bold: true };
  sheet.columns.forEach((column) => {
    column.width = 24;
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const fileName = `Controle_${sanitizeFileName(folder?.nom || control?.name)}.xlsx`;
  saveAs(new Blob([buffer], { type: "application/octet-stream" }), fileName);
};

export default exportControlToExcel;
